import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { TextInput, Button, Text, Card, Chip } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const EstadoSolicitudScreen = ({ navigation, route }) => {
  const [solicitudId, setSolicitudId] = useState(route.params?.solicitudId?.toString() || '');
  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (route.params?.solicitudId) {
      handleConsultar();
    }
  }, []);

  const handleConsultar = async () => {
    if (!solicitudId.trim()) {
      Alert.alert('Error', 'Ingresa el ID de tu solicitud');
      return;
    }

    setLoading(true);
    try {
      const data = await api.getEstadoSolicitud(solicitudId.trim());
      setSolicitud(data);
    } catch (error) {
      Alert.alert('Error', error.message || 'No se encontro la solicitud');
      setSolicitud(null);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoConfig = (estado) => {
    const configs = {
      pendiente: { color: '#FF9800', icon: 'time-outline', label: 'Pendiente de Revision' },
      aprobada: { color: '#4CAF50', icon: 'checkmark-circle-outline', label: 'Aprobada' },
      rechazada: { color: '#f44336', icon: 'close-circle-outline', label: 'Rechazada' },
    };
    return configs[estado] || configs.pendiente;
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Button icon="arrow-left" onPress={() => navigation.goBack()} textColor="#007bff">
          Volver
        </Button>
        <Text style={styles.headerTitle}>Estado de Solicitud</Text>
      </View>

      <Card style={styles.searchCard}>
        <Card.Content>
          <Text style={styles.searchLabel}>Ingresa el ID de tu solicitud para consultar su estado</Text>
          <TextInput
            label="ID de solicitud"
            value={solicitudId}
            onChangeText={setSolicitudId}
            style={styles.input}
            keyboardType="numeric"
            left={<TextInput.Icon icon="pound" />}
          />
          <Button
            mode="contained"
            onPress={handleConsultar}
            loading={loading}
            disabled={loading}
            style={styles.searchButton}
          >
            Consultar
          </Button>
        </Card.Content>
      </Card>

      {solicitud && (
        <Card style={styles.resultCard}>
          <Card.Content>
            {/* Estado */}
            {(() => {
              const config = getEstadoConfig(solicitud.estado);
              return (
                <View style={styles.estadoContainer}>
                  <Ionicons name={config.icon} size={48} color={config.color} />
                  <Text style={[styles.estadoText, { color: config.color }]}>{config.label}</Text>
                  <Chip style={[styles.estadoChip, { backgroundColor: config.color + '20' }]} textStyle={{ color: config.color }}>
                    {solicitud.estado?.toUpperCase()}
                  </Chip>
                </View>
              );
            })()}

            {/* Detalles */}
            <View style={styles.detailsContainer}>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Empresa:</Text>
                <Text style={styles.detailValue}>{solicitud.nombre_empresa}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Codigo:</Text>
                <Text style={styles.detailValue}>{solicitud.slug}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Email:</Text>
                <Text style={styles.detailValue}>{solicitud.email_contacto}</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Fecha solicitud:</Text>
                <Text style={styles.detailValue}>
                  {solicitud.created_at ? new Date(solicitud.created_at).toLocaleDateString('es-HN') : '-'}
                </Text>
              </View>
              {solicitud.plan_nombre && (
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Plan:</Text>
                  <Text style={styles.detailValue}>{solicitud.plan_nombre}</Text>
                </View>
              )}
            </View>

            {/* Mensaje de rechazo */}
            {solicitud.estado === 'rechazada' && solicitud.notas_admin && (
              <Card style={styles.rejectCard}>
                <Card.Content>
                  <Text style={styles.rejectTitle}>Motivo del rechazo:</Text>
                  <Text style={styles.rejectText}>{solicitud.notas_admin}</Text>
                </Card.Content>
              </Card>
            )}

            {/* Mensaje de aprobacion */}
            {solicitud.estado === 'aprobada' && (
              <Card style={styles.approveCard}>
                <Card.Content>
                  <Ionicons name="checkmark-done" size={24} color="#4CAF50" />
                  <Text style={styles.approveText}>
                    Tu empresa ha sido aprobada. Ya puedes iniciar sesion con tu email y contrasena usando el codigo de empresa: {solicitud.slug}
                  </Text>
                </Card.Content>
              </Card>
            )}

            {/* Mensaje pendiente */}
            {solicitud.estado === 'pendiente' && (
              <Card style={styles.pendingCard}>
                <Card.Content>
                  <Ionicons name="hourglass-outline" size={24} color="#FF9800" />
                  <Text style={styles.pendingText}>
                    Tu solicitud esta siendo revisada por un administrador. Te notificaremos cuando sea procesada.
                  </Text>
                </Card.Content>
              </Card>
            )}
          </Card.Content>
        </Card>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  content: { padding: 16, paddingBottom: 40 },
  header: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#333', marginLeft: 8 },
  searchCard: { marginBottom: 16, backgroundColor: 'white' },
  searchLabel: { fontSize: 14, color: '#666', marginBottom: 12 },
  input: { marginBottom: 12, backgroundColor: '#f9f9f9' },
  searchButton: { backgroundColor: '#007bff', paddingVertical: 4 },
  resultCard: { backgroundColor: 'white' },
  estadoContainer: { alignItems: 'center', paddingVertical: 20 },
  estadoText: { fontSize: 20, fontWeight: 'bold', marginTop: 8 },
  estadoChip: { marginTop: 8 },
  detailsContainer: { marginTop: 16 },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  detailLabel: { fontSize: 14, color: '#666', fontWeight: '500' },
  detailValue: { fontSize: 14, color: '#333', fontWeight: '600' },
  rejectCard: { marginTop: 16, backgroundColor: '#FFF3F0' },
  rejectTitle: { fontSize: 14, fontWeight: 'bold', color: '#f44336', marginBottom: 4 },
  rejectText: { fontSize: 14, color: '#666' },
  approveCard: { marginTop: 16, backgroundColor: '#F0FFF4' },
  approveText: { fontSize: 14, color: '#333', marginTop: 8, lineHeight: 20 },
  pendingCard: { marginTop: 16, backgroundColor: '#FFF8E1' },
  pendingText: { fontSize: 14, color: '#333', marginTop: 8, lineHeight: 20 },
});

export default EstadoSolicitudScreen;
